/**
 * Trip-scoped QR invite tokens — mirrors iOS QRCodeService + CommunityAPI.createQRCodeToken.
 * POST /community/qrcode-tokens
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import { getConsumerWebOrigin } from '../utils/consumerWebOrigin';
import { normalizedTripIdString } from '../utils/tripShareInviteMatch';

export class QrCodeApiError extends Error {
  status: number;
  body?: string;

  constructor(status: number, message: string, body?: string) {
    super(message);
    this.name = 'QrCodeApiError';
    this.status = status;
    this.body = body;
  }
}

export interface QrTokenContext {
  token: string;
  hostUserId: string;
  createdAt: string | null;
  expiresAt: string | null;
  tripId: string | null;
  tripEndDate: string | null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Format a trip end date for the QR token API.
 * `YYYY-MM-DD` becomes end of that civil day in UTC (23:59:59.999Z)
 * so the code stays valid through the last day of the trip.
 * Already-ISO strings are passed through when parseable.
 */
export function formatTripEndDateForQr(ymdOrIso: string): string {
  const raw = String(ymdOrIso ?? '').trim();
  if (!raw) {
    throw new QrCodeApiError(400, 'Trip end date is required for trip-scoped QR codes.');
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw}T23:59:59.999Z`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    throw new QrCodeApiError(400, 'Trip end date is required for trip-scoped QR codes.');
  }
  return parsed.toISOString();
}

/** Camera-scannable QR Universal Link for a live QR token (iOS AppConfig.qrInvitationURL). */
export function qrInvitationUrl(token: string): string {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return '';
  const encoded = encodeURIComponent(trimmed).replace(/%2F/gi, '/');
  return `${getConsumerWebOrigin()}/qr/${encoded}`;
}

export async function createQrCodeToken(params: {
  tripId: string;
  tripEndDate: string;
  role: string;
}): Promise<QrTokenContext> {
  const tripId = normalizedTripIdString(params.tripId);
  if (!tripId) {
    throw new QrCodeApiError(400, 'Trip id is required for trip-scoped QR codes.');
  }
  const endRaw = String(params.tripEndDate ?? '').trim();
  if (!endRaw) {
    throw new QrCodeApiError(400, 'Trip end date is required for trip-scoped QR codes.');
  }
  const tripEndDate = formatTripEndDateForQr(endRaw);
  const role = String(params.role ?? 'viewer').trim() || 'viewer';

  const headers = await buildAuthHeaders(true);
  const res = await fetch(`${API_BASE_URL}/community/qrcode-tokens`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tripId, tripEndDate, role }),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new QrCodeApiError(res.status, `Failed to create QR token (${res.status})`, body);
  }

  const json = asRecord(await res.json());
  const token = String(json.token ?? '').trim();
  if (!token) {
    throw new QrCodeApiError(500, 'QR token created without token value');
  }

  return {
    token,
    hostUserId: String(json.hostUserId ?? json.host_user_id ?? '').trim(),
    createdAt: json.createdAt != null ? String(json.createdAt) : null,
    expiresAt: json.expiresAt != null ? String(json.expiresAt) : null,
    tripId,
    tripEndDate,
  };
}
